import { create } from 'zustand';
import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import type { TopicoCompacto } from '@/components/topicos/core/models';
import type { TopicoState } from '@/components/topicos/core/topico_repository';
import { TopicoRepositoryImpl } from '@/components/topicos/data/topico_repository';

export interface FetchTopicosOptions {
  /** Filtra apenas tópicos vinculados a estas etiquetas (ex.: etiqueta da ouvidoria atual). */
  etiquetas?: string[] | null;
  /** Filtra por município (opcional). */
  municipioId?: number | string | null;
  /** Filtra por status: Ativo | Inativo | Arquivado. */
  status?: string | null;
}

interface TopicosState {
  topicos: TopicoCompacto[];
  isLoading: boolean;
  error: string | null;
  fetchTopicos: (options?: FetchTopicosOptions) => Promise<void>;
  addTopico: (payload: TopicoState) => Promise<string | null>;
}

const api = new RemoteDataSource(Settings.urlBase);
const repo = new TopicoRepositoryImpl();

const sanitizeEtiquetas = (etiquetas?: string[] | null): string[] => {
  if (!Array.isArray(etiquetas)) return [];
  return etiquetas
    .map((e) => (typeof e === 'string' ? e.trim() : ''))
    .filter((e): e is string => e.length > 0);
};

export const useTopicosStore = create<TopicosState>((set) => ({
  topicos: [],
  isLoading: false,
  error: null,

  fetchTopicos: async (options) => {
    set({ isLoading: true, error: null });

    try {
      const etiquetas = sanitizeEtiquetas(options?.etiquetas);
      const useEtiquetasEndpoint = etiquetas.length > 0;

      const baseParams: Record<string, unknown> = {};
      if (options?.municipioId != null && options.municipioId !== '') {
        baseParams.municipioId = options.municipioId;
      }
      if (options?.status) {
        baseParams.status = options.status;
      }

      const serialize = (data: unknown): unknown[] => {
        if (Array.isArray(data)) return data;
        if (data && typeof data === 'object' && 'items' in data) return (data as { items: unknown[] }).items;
        if (data && typeof data === 'object' && 'data' in data) return (data as { data: unknown[] }).data;
        return [];
      };

      let items: unknown[] = [];

      if (useEtiquetasEndpoint) {
        // OpenAPI: GET /urbnet/etiquetas?etiquetas=<string>
        // O parâmetro aceita apenas UMA etiqueta por requisição. Quando a ouvidoria
        // possui múltiplas etiquetas, disparamos N requisições em paralelo e
        // mesclamos os resultados deduplicando por id.
        const responses = await Promise.all(
          etiquetas.map((etiqueta) =>
            api.get<unknown[]>({
              endpoint: 'etiquetas',
              headers: { accept: 'application/vnd.data.compact.v1+json' },
              queryParams: { ...baseParams, etiquetas: etiqueta },
              forceRefresh: true,
              serialize,
            }),
          ),
        );

        const errors: string[] = [];
        const seen = new Set<string>();
        const merged: unknown[] = [];

        responses.forEach((res, idx) => {
          if ('failure' in res) {
            errors.push(`[${etiquetas[idx]}] ${res.failure.message}`);
            return;
          }
          const arr = (res.success.data || []) as unknown[];
          arr.forEach((raw) => {
            const obj = raw as { id?: unknown } | null | undefined;
            const id = obj && typeof obj === 'object' ? obj.id : undefined;
            const key = typeof id === 'string' && id ? id : `__anon_${merged.length}`;
            if (seen.has(key)) return;
            seen.add(key);
            merged.push(raw);
          });
        });

        // Se TODAS as requisições falharam, propaga erro. Se apenas algumas, mostra parcial.
        if (errors.length === responses.length) {
          throw new Error(errors.join(' | '));
        }

        items = merged;
        set({
          topicos: items as TopicoCompacto[],
          isLoading: false,
          error: errors.length ? errors.join(' | ') : null,
        });
        return;
      }

      const result = await api.get<unknown[]>({
        endpoint: 'v1/topicos',
        headers: { accept: 'application/vnd.data.compact.v1+json' },
        queryParams: Object.keys(baseParams).length ? baseParams : undefined,
        forceRefresh: true,
        serialize,
      });

      if ('failure' in result) {
        throw new Error(result.failure.message);
      }

      items = (result.success.data || []) as unknown[];
      set({ topicos: items as TopicoCompacto[], isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar tópicos';
      set({ isLoading: false, error: message });
    }
  },

  addTopico: async (payload) => {
    set({ isLoading: true, error: null });

    try {
      const result = await repo.adicionarTopico(payload);

      if (result.failure && result.failure.messages && result.failure.messages.length > 0) {
        throw new Error(result.failure.messages[0]);
      }

      // O caller é responsável por chamar fetchTopicos com o filtro adequado
      // (ex.: etiquetas da ouvidoria atual). Evita um refetch sem filtro aqui.
      set({ isLoading: false, error: null });
      return result.success;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao criar tópico';
      set({ isLoading: false, error: message });
      throw new Error(message);
    }
  },
}));
